import React from "react";
import { LineItemElementProps } from "../types";

interface Props {
	position: { top: number; left: number };
	variablesWindowVariables: string[];
	onVariablesChange: (vars: string[]) => void;
	allowEmptyValue: boolean;
	onAllowEmptyValueChange: (val: boolean) => void;
	conditional: boolean;
	onConditionalChange: (val: boolean) => void;
	defaultVariableValue: string;
	onDefaultVariableValueChange: (val: string) => void;
	useFixedPosition: boolean;
	inputRef: React.RefObject<HTMLInputElement>;
	onCancel: () => void;
	onDelete: () => void;
	onSave: () => void;
	lineItemToProcess: LineItemElementProps[];
	editingIndex: number | null;
	onLineItemChange: (elements: LineItemElementProps[]) => void;
}

const VariableCreationPopup: React.FC<Props> = ({
	position,
	variablesWindowVariables,
	onVariablesChange,
	allowEmptyValue,
	onAllowEmptyValueChange,
	conditional,
	onConditionalChange,
	defaultVariableValue,
	onDefaultVariableValueChange,
	useFixedPosition,
	inputRef,
	onCancel,
	onDelete,
	onSave,
	lineItemToProcess,
	editingIndex,
	onLineItemChange,
}) => {

	// should only be able to save if there's a default value or allow empty value is true
	const canSave = variablesWindowVariables.length && (defaultVariableValue.trim() || allowEmptyValue);

	const cleanupDependenciesForDeletedValue = (deletedValue: string) => {
		if (editingIndex === null) return;

		const deletedNorm = deletedValue.trim().toLowerCase();

		const newLineItem = (lineItemToProcess ?? []).map((element, i) => {
			if (!element.valueDependencies) return element;

			let newDeps = { ...element.valueDependencies };

			if (i === editingIndex) {
				// Remove this value as a key in the edited element's dependencies
				Object.keys(newDeps).forEach((key) => {
					if (key.trim().toLowerCase() === deletedNorm) delete newDeps[key];
				});
			}

			// All elements: remove this value from parentValues where they reference the edited element
			newDeps = Object.fromEntries(
				Object.entries(newDeps)
					.map(([key, deps]) => {
						const cleanedDeps = deps
							.map((dep) => {
								if (dep.elementIndex !== editingIndex) return dep;
								return { ...dep, parentValues: dep.parentValues.filter((pv) => pv.trim().toLowerCase() !== deletedNorm) };
							})
							.filter((dep) => dep.parentValues.length > 0);
						return [key, cleanedDeps] as [string, typeof cleanedDeps];
					})
					.filter(([_, deps]) => deps.length > 0)
			);

			if (Object.keys(newDeps).length === 0) {
				const { valueDependencies, ...rest } = element;
				return rest;
			}
			return { ...element, valueDependencies: newDeps };
		});

		onLineItemChange(newLineItem);
	};

	return (
		<div
			id="create-variable-modal"
			style={{
				position: useFixedPosition ? "fixed" : "absolute",
				top: position.top,
				left: Math.max(0, position.left - 200),
			}}
			className="w-[400px]  flex-col justify-start items-center inline-flex"
		>
			<img src="/assets/ui/up.svg" className="w-7 h-7 relative" />
			<div className="bg-white shadow  rounded-xl flex-col justify-start items-center flex">
				<div className="self-stretch  bg-white flex-col justify-start items-center flex">
					<div className="self-stretch  px-6 pt-6 bg-white flex-col justify-start items-start gap-4 flex">
						<div className="self-stretch  flex-col justify-start items-start gap-1 flex">
							<div className="self-stretch text-gray-900 text-lg font-semibold font-['Hanken Grotesk'] leading-7">Create variable</div>
						</div>
					</div>
					<div className="self-stretch h-5" />
				</div>
				<div className="self-stretch px-6 opacity-80 flex-col justify-start items-start gap-5 flex">
					<div className="self-stretch  flex-col justify-start items-start gap-3 flex">
						<div className="self-stretch  flex-col justify-start items-start gap-0.5 flex">
							<div className="self-stretch h-[52px] flex-col justify-start items-start gap-1.5 flex">
								<div className="self-stretch px-3.5 py-2.5 bg-white rounded-lg shadow border border-gray-300 justify-start items-center gap-2 inline-flex">
									<div className="grow shrink basis-0 h-6 justify-start items-center gap-2 flex">
										<input
											ref={inputRef}
											className="text-gray-500 text-base font-normal font-['Hanken Grotesk'] leading-normal"
											placeholder="Variable value"
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													if (!inputRef.current!.value.trim()) return;
													onVariablesChange([...variablesWindowVariables, inputRef.current!.value.trim()]);
													inputRef.current!.value = "";
												}
											}}
										/>
									</div>
									<div
										className="justify-end items-center flex"
										onClick={() => {
											if (!inputRef.current!.value.trim()) return;
											onVariablesChange([...variablesWindowVariables, inputRef.current!.value.trim()]);
											inputRef.current!.value = "";
										}}
									>
										<img src="/assets/ui/plus-circle.svg" className="w-8 h-8 relative" />
									</div>
								</div>
							</div>
						</div>
						<div className=" justify-start items-start gap-2 flex flex-wrap ">
							{variablesWindowVariables.map((variable, i) => {
								return (
									<div
										key={i}
										className="pl-2.5 pr-2 py-0.5 mix-blend-multiply bg-sky-50 rounded border border-blue-100 justify-start items-center gap-1 flex"
									>
										<div
											className="w-3 h-3 relative cursor-pointer"
											onClick={() => {
												if (defaultVariableValue.trim() === variable.trim()) {
													onDefaultVariableValueChange("");
												} else {
													onDefaultVariableValueChange(variable);
												}
											}}
										>
											<img
												src={
													defaultVariableValue.trim() === variable.trim()
														? `/assets/ui/bell-02.svg`
														: `/assets/ui/bell-01.svg`
												}
												className="w-3 h-3 relative"
											/>
										</div>
										<div className="text-center text-slate-800 text-sm font-medium font-['Hanken Grotesk'] leading-tight whitespace-nowrap">
											{variable}
										</div>
										{/** DELETE VARIABLE VALUE BUTTON */}
										<div
											className="w-3 h-3 relative cursor-pointer"
											onClick={() => {
												if (defaultVariableValue.trim() === variable.trim()) {
													onDefaultVariableValueChange("");
												}
												cleanupDependenciesForDeletedValue(variable);
												onVariablesChange(variablesWindowVariables.filter((_, index) => index !== i));
											}}
										>
											<img src="/assets/ui/x-close.svg" className="w-3 h-3 relative" />
										</div>
									</div>
								);
							})}
						</div>
					</div>
					<div className="w-[344px] pt-4 justify-start items-start gap-2 inline-flex">
						<div className="pt-0.5 justify-center items-center flex">
							<input
								type="checkbox"
								className="w-4 h-4 relative bg-white rounded border border-gray-300"
								checked={allowEmptyValue}
								onChange={(e) => {
									onAllowEmptyValueChange(e.target.checked);
								}}
							/>
						</div>
						<div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
							<div className="self-stretch text-gray-900 text-sm font-medium font-['Hanken Grotesk'] leading-tight select-none">
								Allow empty value
							</div>
						</div>
						<div className="pt-0.5 justify-center items-center flex">
							<input
								type="checkbox"
								className="w-4 h-4 relative bg-white rounded border border-gray-300"
								checked={conditional}
								onChange={(e) => {
									onConditionalChange(e.target.checked);
								}}
							/>
						</div>
						<div className="grow shrink basis-0 flex-col justify-start items-start inline-flex">
							<div className="self-stretch text-gray-900 text-sm font-medium font-['Hanken Grotesk'] leading-tight select-none">
								Conditional{" "}
							</div>
						</div>
					</div>
				</div>
				<div className="self-stretch h-[100px] pt-8 flex-col justify-start items-start flex">
					<div className="self-stretch px-6 pb-6 justify-start items-start gap-3 inline-flex select-none">
						<div
							className="grow shrink basis-0 h-11 px-[18px] py-2.5 bg-white rounded-lg border border-gray-200 justify-center items-center gap-2 flex cursor-pointer"
							onClick={onCancel}
						>
							<div className="text-slate-700 text-base font-semibold font-['Hanken Grotesk'] leading-normal">Cancel</div>
						</div>
						<div
							className="grow shrink basis-0 h-11 px-[18px] py-2.5 bg-blue-900 rounded-lg shadow border border-blue-900 justify-center items-center gap-2 flex cursor-pointer"
							onClick={() => {
								const confirmResult = window.confirm("Are you sure you want to delete this variable?");
								if (confirmResult) {
									onDelete();
								}
							}}
						>
							<div className="text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal">Delete</div>
						</div>
						<div className="grow shrink basis-0 relative group">
							{!canSave && (
								<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
									You must set a default variable value or allow empty value
								</div>
							)}
							<div
								className={`${
									canSave ? "bg-blue-900" : "bg-gray-200"
								} w-full h-11 px-[18px] py-2.5 rounded-lg shadow border border-blue-900 justify-center items-center gap-2 flex cursor-pointer`}
								onClick={() => {
									canSave && onSave();
								}}
							>
								<div className="text-white text-base font-semibold font-['Hanken Grotesk'] leading-normal">Save</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VariableCreationPopup;
